import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Tag, Users, ShieldCheck, Edit3, Gift, X, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Panel, EmptyState } from "@/components/common";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const ENTITLEMENT_LABELS = {
  ai_cfo: "AI CFO", ai_marketing: "AI Marketing manager", ai_seo: "AI SEO specialist",
  accounting_sync: "QuickBooks / Xero sync", email_briefing: "Morning briefing email",
};

const DEFAULT_PRICE = { lookup_key: "", amount: 29, currency: "usd", interval: "month" };

const DEFAULT_PLAN = {
  name: "", description: "", features: ["Everything in Starter"], is_public: true, sort_order: 0,
  entitlements: { ai_cfo: true, ai_marketing: true, ai_seo: true, accounting_sync: true, email_briefing: true, seats: 25 },
  prices: [{ ...DEFAULT_PRICE }],
};

const statusLabel = (ent) => {
  const s = ent?.status;
  if (!s) return "—";
  const map = { trialing: "Trial", active: "Active", granted_free: "Granted free",
                past_due: "Past due", canceled: "Canceled", trial_expired: "Trial expired" };
  return map[s] || s;
};

const statusCls = (s) => ({
  active: "bg-emerald-100 text-emerald-900 border-emerald-200",
  trialing: "bg-sky-100 text-sky-900 border-sky-200",
  granted_free: "bg-violet-100 text-violet-900 border-violet-200",
  past_due: "bg-amber-100 text-amber-900 border-amber-200",
  canceled: "bg-rose-100 text-rose-900 border-rose-200",
  trial_expired: "bg-rose-100 text-rose-900 border-rose-200",
}[s] || "");

export default function Admin() {
  const [tab, setTab] = useState("plans");
  return (
    <div data-testid="admin-page">
      <PageHeader title="Platform admin" subtitle="Manage subscription plans, grant free access, and oversee companies on the platform." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="plans" data-testid="admin-tab-plans"><Tag className="h-4 w-4 mr-1.5" />Plans</TabsTrigger>
          <TabsTrigger value="companies" data-testid="admin-tab-companies"><Users className="h-4 w-4 mr-1.5" />Companies</TabsTrigger>
        </TabsList>
        <TabsContent value="plans" className="mt-5"><PlansManager /></TabsContent>
        <TabsContent value="companies" className="mt-5"><CompaniesManager /></TabsContent>
      </Tabs>
    </div>
  );
}

function PlansManager() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    setPlans(null);
    try { const { data } = await api.get("/admin/plans"); setPlans(data); } catch (e) { toast.error(errMsg(e)); setPlans([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete the “${p.name}” plan? Companies already subscribed are not affected.`)) return;
    try { await api.delete(`/admin/plans/${p.plan_id}`); toast.success("Plan deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (plans === null) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{plans.length} plan{plans.length === 1 ? "" : "s"} · Plans sync to Stripe automatically.</p>
        <Button size="sm" onClick={() => setEditing({ ...DEFAULT_PLAN })} data-testid="admin-new-plan-button"><Plus className="h-4 w-4 mr-1" />New plan</Button>
      </div>
      {plans.length === 0 ? (
        <EmptyState icon={Tag} title="No plans yet" text="Create your first plan to let companies subscribe."
          action={<Button onClick={() => setEditing({ ...DEFAULT_PLAN })} data-testid="admin-empty-new-plan-button"><Plus className="h-4 w-4 mr-1" />Create a plan</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-plans-grid">
          {plans.map((p) => (
            <div key={p.plan_id} className="bg-card border border-border rounded-xl p-5" data-testid="admin-plan-card">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-display font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.description || "—"}</p>
                </div>
                <Badge variant={p.is_public ? "default" : "secondary"}>{p.is_public ? "Public" : "Private"}</Badge>
              </div>
              <ul className="mt-3 space-y-1">
                {(p.prices || []).map((pr) => (
                  <li key={pr.lookup_key} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{pr.interval ? `per ${pr.interval}` : "one-time"}</span>
                    <span className="font-semibold">{(pr.currency || "usd").toUpperCase()} {Number(pr.amount).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap gap-1">
                {Object.entries(p.entitlements || {}).filter(([, v]) => v).slice(0, 6).map(([k]) => (
                  <Badge key={k} variant="outline" className="font-normal">{ENTITLEMENT_LABELS[k] || k}</Badge>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => setEditing(JSON.parse(JSON.stringify(p)))} data-testid="admin-plan-edit-button"><Edit3 className="h-4 w-4 mr-1.5" />Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => remove(p)} data-testid="admin-plan-delete-button"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <PlanEditor plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function PlanEditor({ plan, onClose, onSaved }) {
  const [form, setForm] = useState(plan);
  const [busy, setBusy] = useState(false);
  const isNew = !plan.plan_id;

  const setPrice = (i, patch) => setForm((f) => ({ ...f, prices: f.prices.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  const addPrice = () => setForm((f) => ({ ...f, prices: [...f.prices, { ...DEFAULT_PRICE, lookup_key: "" }] }));
  const removePrice = (i) => setForm((f) => ({ ...f, prices: f.prices.filter((_, idx) => idx !== i) }));
  const toggleEnt = (k) => setForm((f) => ({ ...f, entitlements: { ...f.entitlements, [k]: !f.entitlements[k] } }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the plan a name");
    for (const p of form.prices) {
      if (!p.lookup_key.trim()) return toast.error("Every price needs a lookup key");
      if (!(p.amount > 0)) return toast.error("Amount must be greater than 0");
    }
    setBusy(true);
    try {
      const payload = { ...form, prices: form.prices.map((p) => ({ ...p, amount: Number(p.amount) })) };
      if (isNew) await api.post("/admin/plans", payload);
      else await api.patch(`/admin/plans/${plan.plan_id}`, payload);
      toast.success("Plan saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-plan-editor">
        <DialogHeader><DialogTitle>{isNew ? "New plan" : `Edit ${plan.name}`}</DialogTitle>
          <DialogDescription>Plans sync to Stripe products & prices. Change prices by adding a new one — old ones stay for existing subscribers.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-plan-name-input" /></div>
            <div className="space-y-1.5"><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-plan-description-input" /></div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: v })} data-testid="admin-plan-public-switch" />
            <Label className="text-sm font-normal">Available for self-serve subscription</Label>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2"><Label>Prices</Label>
              <Button type="button" size="sm" variant="ghost" onClick={addPrice} data-testid="admin-plan-add-price"><Plus className="h-4 w-4 mr-1" />Add price</Button>
            </div>
            <div className="space-y-2">
              {form.prices.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid="admin-plan-price-row">
                  <div className="col-span-4 space-y-1"><Label className="text-xs">Lookup key</Label><Input value={p.lookup_key} onChange={(e) => setPrice(i, { lookup_key: e.target.value.replace(/[^a-z0-9_-]/gi, "_").toLowerCase() })} placeholder="starter_monthly" data-testid="admin-price-lookup-input" /></div>
                  <div className="col-span-3 space-y-1"><Label className="text-xs">Amount</Label><Input type="number" min="0" step="0.01" value={p.amount} onChange={(e) => setPrice(i, { amount: e.target.value })} data-testid="admin-price-amount-input" /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Currency</Label>
                    <Select value={p.currency} onValueChange={(v) => setPrice(i, { currency: v })}>
                      <SelectTrigger data-testid="admin-price-currency-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="usd">USD</SelectItem><SelectItem value="eur">EUR</SelectItem><SelectItem value="gbp">GBP</SelectItem><SelectItem value="inr">INR</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Interval</Label>
                    <Select value={p.interval || "onetime"} onValueChange={(v) => setPrice(i, { interval: v === "onetime" ? null : v })}>
                      <SelectTrigger data-testid="admin-price-interval-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="month">Monthly</SelectItem><SelectItem value="year">Yearly</SelectItem><SelectItem value="onetime">One-time</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1"><Button type="button" variant="ghost" size="icon" onClick={() => removePrice(i)} disabled={form.prices.length <= 1} data-testid="admin-price-remove-button"><Trash2 className="h-4 w-4" /></Button></div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Entitlements</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.keys(ENTITLEMENT_LABELS).map((k) => (
                <label key={k} className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2 cursor-pointer" data-testid={`admin-plan-ent-${k}`}>
                  <Checkbox checked={!!form.entitlements[k]} onCheckedChange={() => toggleEnt(k)} />
                  <span className="text-sm">{ENTITLEMENT_LABELS[k]}</span>
                </label>
              ))}
              <label className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2 col-span-full">
                <span className="text-sm w-24">Max seats</span>
                <Input type="number" min="1" value={form.entitlements.seats || ""} onChange={(e) => setForm({ ...form, entitlements: { ...form.entitlements, seats: e.target.value ? Number(e.target.value) : null } })} placeholder="unlimited" className="h-8" data-testid="admin-plan-seats-input" />
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="admin-plan-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}{isNew ? "Create plan" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompaniesManager() {
  const [cos, setCos] = useState(null);
  const [plans, setPlans] = useState([]);
  const [grant, setGrant] = useState(null);

  const load = useCallback(async () => {
    setCos(null);
    try {
      const [c, p] = await Promise.all([api.get("/admin/companies"), api.get("/admin/plans")]);
      setCos(c.data); setPlans(p.data);
    } catch (e) { toast.error(errMsg(e)); setCos([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (co) => {
    if (!window.confirm(`Revoke granted-free status for ${co.name}? They'll go back to a 14-day trial.`)) return;
    try { await api.post("/admin/revoke-free", { company_id: co.company_id }); toast.success("Reverted to trial"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (cos === null) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (cos.length === 0) return <EmptyState icon={Users} title="No companies yet" text="Companies that sign up will show up here." />;

  return (
    <>
      <Panel title="Companies" icon={Users} testid="admin-companies-panel">
        <div className="-mx-5 sm:-mx-6 overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="admin-companies-table">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
              <th className="px-5 sm:px-6 py-2 font-medium">Company</th>
              <th className="py-2 font-medium">Status</th>
              <th className="py-2 font-medium">Plan</th>
              <th className="py-2 font-medium">Members</th>
              <th className="py-2 font-medium">Updated</th>
              <th className="py-2 font-medium px-5 sm:px-6 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {cos.map((c) => (
                <tr key={c.company_id} className="border-b border-border/60" data-testid="admin-company-row">
                  <td className="px-5 sm:px-6 py-3">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.industry || c.size || "—"}</p>
                  </td>
                  <td className="py-3"><Badge variant="outline" className={statusCls(c.entitlements?.status) + " font-normal"}>{statusLabel(c.entitlements)}</Badge></td>
                  <td className="py-3">{c.entitlements?.plan_name || "—"}</td>
                  <td className="py-3 tabular-nums">{c.member_count || 0}</td>
                  <td className="py-3 text-xs text-muted-foreground">{c.billing?.updated_at ? fmtDateTime(c.billing.updated_at) : "—"}</td>
                  <td className="py-3 px-5 sm:px-6 text-right whitespace-nowrap">
                    {c.entitlements?.status === "granted_free" ? (
                      <Button variant="ghost" size="sm" onClick={() => revoke(c)} data-testid="admin-revoke-free-button"><X className="h-4 w-4 mr-1" />Revoke</Button>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => setGrant({ company: c, plan_id: null, expires_at: "", note: "" })} data-testid="admin-grant-free-button"><Gift className="h-4 w-4 mr-1.5" />Grant free</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {grant && <GrantDialog grant={grant} plans={plans} onClose={() => setGrant(null)} onSaved={() => { setGrant(null); load(); }} />}
    </>
  );
}

function GrantDialog({ grant, plans, onClose, onSaved }) {
  const [form, setForm] = useState({ plan_id: grant.plan_id, expires_at: grant.expires_at, note: grant.note });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/grant-free", { company_id: grant.company.company_id, plan_id: form.plan_id || null,
        expires_at: form.expires_at || null, note: form.note });
      toast.success(`${grant.company.name} now has free access`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="admin-grant-dialog">
        <DialogHeader><DialogTitle>Grant free access to {grant.company.name}</DialogTitle>
          <DialogDescription>Overrides their subscription state. You can revoke this later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Unlock as plan</Label>
            <Select value={form.plan_id || "all"} onValueChange={(v) => setForm({ ...form, plan_id: v === "all" ? null : v })}>
              <SelectTrigger data-testid="admin-grant-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All features (recommended)</SelectItem>
                {plans.map((p) => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Expires on (optional)</Label>
            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="admin-grant-expires-input" />
            <p className="text-xs text-muted-foreground">Leave blank for permanent free access.</p>
          </div>
          <div className="space-y-1.5"><Label>Note (internal)</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="admin-grant-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="admin-grant-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}<Gift className="h-4 w-4 mr-1.5" />Grant free access</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
